// Cancel confirmation — RON-5466 Phase 3. Prototype: `CancelScreen`.
//
// Serves all three cancel flows (single, group, bulk) because they differ only in how many rows
// are listed. Cancelling is irreversible here, so the screen names every visit it is about to
// close rather than showing a count and hoping the host remembers what they selected.
//
// NOT the same component as VisitorConfirmation — that is the "done" screen shown afterwards.

import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { canCancelVisit, pluralText } from "../visitorConstants.js";
import { VisitorAvatar, VisitorStatusBadge } from "./VisitorBadges.jsx";

const VisitorCancelConfirm = ({
  visits,
  groupSiblings = [],
  includeGroup = false,
  onToggleGroup,
  // §8/§9, 24-October-2026. ON by default, matching the server's own default and the prototype.
  sendEmail = true,
  onToggleEmail,
  isSubmitting,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslation("common");

  // The rows the server will actually act on, so the counts on screen match the outcome.
  const targets = includeGroup ? [...visits, ...groupSiblings] : visits;
  const cancellable = targets.filter(canCancelVisit);
  const skipped = targets.length - cancellable.length;

  if (targets.length === 0) {
    return (
      <div className="visitor-cancel">
        <p className="visitor__placeholder">{t("react_visitors_not_found")}</p>
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
        >
          {t("react_visitors_back_to_list")}
        </button>
      </div>
    );
  }

  return (
    <div className="visitor-cancel">
      <div className="visitor-card">
        <p className="visitor-card__title">
          {pluralText(t, "react_visitors_cancel_confirm_title", cancellable.length)}
        </p>

        <ul className="visitor-review__people">
          {targets.map((visit) => (
            <li className="visitor-cancel__row" key={visit.id}>
              <VisitorAvatar name={visit.name} type={visit.type} />
              <span className="visitor-cancel__name">{visit.name}</span>
              <span className="visitor-cancel__date">
                {formatDateToEEEddMMM(visit.startDate)}
              </span>
              <VisitorStatusBadge status={visit.status} />
            </li>
          ))}
        </ul>

        {/* A mixed selection is not an error — rows that can no longer be cancelled (already
            closed, or the guest has arrived) are simply left alone. §4.9 reports the same thing
            as `skipped`. */}
        {skipped > 0 && (
          <p className="visitor-card__hint">
            {pluralText(t, "react_visitors_cancel_skipped", skipped)}
          </p>
        )}
      </div>

      {/* Offered only when this visit was invited alongside others. Off by default: cancelling
          one person's visit must never silently cancel their colleagues'. */}
      {groupSiblings.length > 0 && (
        <div className="visitor-card">
          <label className="visitor-toggle">
            <input
              type="checkbox"
              checked={includeGroup}
              onChange={onToggleGroup}
              className="visitor-toggle__input"
            />
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {pluralText(t, "react_visitors_cancel_group", groupSiblings.length)}
              </span>
              <span className="visitor-toggle__hint">
                {t("react_visitors_cancel_group_hint")}
              </span>
            </span>
          </label>
        </div>
      )}

      {/* One `visit_cancelled` email per visitor ACTUALLY cancelled — the skipped ones, whose
          guest has already arrived, are not emailed. Which is why this sits below the skipped
          notice: the host has just been told the count it applies to. */}
      {cancellable.length > 0 && (
        <div className="visitor-card">
          <label className="visitor-toggle">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={onToggleEmail}
              className="visitor-toggle__input"
            />
            <span className="visitor-toggle__text">
              <span className="visitor-toggle__label">
                {t(
                  cancellable.length > 1
                    ? "react_visitors_send_cancel_email_all"
                    : "react_visitors_send_cancel_email"
                )}
              </span>
              <span className="visitor-toggle__hint">
                {t(
                  cancellable.length > 1
                    ? "react_visitors_send_cancel_email_all_hint"
                    : "react_visitors_send_cancel_email_hint"
                )}
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="visitor-wizard__actions">
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("react_visitors_back")}
        </button>

        <button
          type="button"
          className="visitor-btn visitor-btn--secondary visitor-btn--danger"
          onClick={onConfirm}
          // Nothing cancellable means nothing to do; the button would only produce an error.
          disabled={isSubmitting || cancellable.length === 0}
        >
          {t("react_visitors_cancel_confirm")}
        </button>
      </div>
    </div>
  );
};

export default VisitorCancelConfirm;
